import { useEffect, useState } from "react";
import { X, ChevronLeft, ChevronRight, Pause, Play, Lightbulb, Kanban, Sparkles, Calendar, Target, BarChart3 } from "lucide-react";

const SCENES = [
  {
    id: "vault",
    eyebrow: "i. Idea Vault",
    title: "Every spark,",
    italic: "kept.",
    body: "Capture an idea in a single keystroke. Tag it, search it, promote it into the pipeline when it's ready.",
    icon: Lightbulb,
    mock: ({ accent, ink }) => (
      <div className="space-y-2">
        {["The 5-minute morning routine that 10x'd my output",
          "Why your hook fails in the first 3 seconds",
          "Notion vs Linear for creators",
          "My $7 mic vs $700 mic blind test"].map((t, i) => (
          <div key={i} className="rounded-lg px-4 py-3 flex items-center justify-between" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${ink}22` }}>
            <span className="text-sm" style={{ color: ink, opacity: 0.92 }}>{t}</span>
            <span className="text-[10px] uppercase tracking-[0.18em]" style={{ color: accent }}>idea</span>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "pipeline",
    eyebrow: "ii. The Pipeline",
    title: "Six rooms,",
    italic: "one craft.",
    body: "Drag an idea through Research → Script → Create → Schedule → Published. The drag itself is the ritual.",
    icon: Kanban,
    mock: ({ accent, ink }) => (
      <div className="grid grid-cols-6 gap-2">
        {["Idea","Research","Script","Create","Sched","Pub"].map((stage, idx) => (
          <div key={stage} className="rounded-lg p-2 min-h-[110px]" style={{ background: "rgba(255,255,255,0.03)", border: `1px solid ${ink}1f` }}>
            <div className="text-[9px] uppercase tracking-[0.2em] mb-2" style={{ color: ink, opacity: 0.5 }}>{stage}</div>
            <div className="space-y-1.5">
              <div className="h-1.5 rounded" style={{ background: idx === 2 ? accent : `${ink}33` }} />
              <div className="h-1.5 rounded" style={{ background: `${ink}33`, width: "70%" }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
  {
    id: "ai",
    eyebrow: "iii. AI co-pilot",
    title: "A writer with",
    italic: "taste.",
    body: "Click 'Write script' on any idea — PintaFlow's AI drafts Hook, Intro, Body and CTA tuned to your platform.",
    icon: Sparkles,
    mock: ({ accent, ink }) => (
      <div className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${ink}22` }}>
        <div className="text-[10px] uppercase tracking-[0.22em] mb-3" style={{ color: accent }}>Hook</div>
        <div className="text-sm leading-relaxed" style={{ color: ink, opacity: 0.92 }}>
          [STOP SCROLLING] Your hook didn&apos;t fail — it <em style={{ color: accent }}>died</em> in the first 3 seconds. Here&apos;s why.
        </div>
        <div className="text-[10px] uppercase tracking-[0.22em] mt-5 mb-2" style={{ color: accent }}>Intro</div>
        <div className="text-xs leading-relaxed" style={{ color: ink, opacity: 0.7 }}>
          80% of creators spend 4 hours on a video and 30 seconds on the hook. Then they wonder why retention tanks…
        </div>
        <div className="mt-4 flex gap-2">
          <div className="px-2 py-0.5 rounded text-[10px]" style={{ background: `${accent}1a`, color: accent }}>gpt-5.2</div>
          <div className="px-2 py-0.5 rounded text-[10px]" style={{ background: `${ink}1a`, color: ink, opacity: 0.6 }}>shorts</div>
          <div className="px-2 py-0.5 rounded text-[10px]" style={{ background: `${ink}1a`, color: ink, opacity: 0.6 }}>punchy</div>
        </div>
      </div>
    ),
  },
  {
    id: "calendar",
    eyebrow: "iv. The calendar",
    title: "A publishing month,",
    italic: "at rest.",
    body: "See your scheduled posts and content beats laid out across the month. No more guessing what comes next.",
    icon: Calendar,
    mock: ({ accent, ink }) => (
      <div>
        <div className="grid grid-cols-7 gap-1 mb-1">
          {["S","M","T","W","T","F","S"].map((d, i) => (
            <div key={i} className="text-[10px] text-center uppercase tracking-[0.2em]" style={{ color: ink, opacity: 0.4 }}>{d}</div>
          ))}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {Array.from({ length: 28 }).map((_, i) => {
            const has = [3, 7, 11, 14, 18, 22, 25].includes(i);
            return (
              <div key={i} className="aspect-square rounded flex items-center justify-center text-[10px]"
                style={{ background: has ? `${accent}26` : `${ink}10`, color: has ? accent : ink, opacity: has ? 1 : 0.55, border: has ? `1px solid ${accent}66` : "none" }}>
                {i + 1}
              </div>
            );
          })}
        </div>
      </div>
    ),
  },
  {
    id: "analytics",
    eyebrow: "v. Analytics",
    title: "Numbers tell a",
    italic: "story.",
    body: "Log views, likes and subs. See which platform is moving and which is quiet — week over week.",
    icon: BarChart3,
    mock: ({ accent, ink }) => {
      const pts = [12, 18, 14, 22, 20, 28, 32, 26, 36, 42, 38, 48, 44, 56, 52, 64];
      const max = 64;
      const w = 400, h = 80;
      const step = w / (pts.length - 1);
      const path = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${(i * step).toFixed(1)} ${(h - (p / max) * (h - 6)).toFixed(1)}`).join(" ");
      const area = `${path} L ${w} ${h} L 0 ${h} Z`;
      return (
        <div className="rounded-lg p-5" style={{ background: "rgba(255,255,255,0.04)", border: `1px solid ${ink}22` }}>
          <div className="flex items-baseline justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em]" style={{ color: ink, opacity: 0.5 }}>Views · last 30d</div>
              <div className="text-2xl mt-1" style={{ fontFamily: "'Fraunces', serif", color: ink }}>110.8k</div>
            </div>
            <div className="text-xs" style={{ color: accent }}>+38% ↗</div>
          </div>
          <svg viewBox={`0 0 ${w} ${h}`} className="w-full" style={{ overflow: "visible" }}>
            <defs>
              <linearGradient id="lpf" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={accent} stopOpacity="0.35" />
                <stop offset="100%" stopColor={accent} stopOpacity="0" />
              </linearGradient>
            </defs>
            <path d={area} fill="url(#lpf)" />
            <path d={path} fill="none" stroke={accent} strokeWidth="1.6" />
          </svg>
        </div>
      );
    },
  },
  {
    id: "goals",
    eyebrow: "vi. Goals",
    title: "Your rhythm,",
    italic: "your terms.",
    body: "Set a quiet number — 8 posts a month, 1 essay a week — and let PintaFlow gently keep score.",
    icon: Target,
    mock: ({ accent, ink }) => (
      <div className="space-y-3">
        {[
          { label: "8 posts this month", pct: 50, ratio: "4/8" },
          { label: "12 Reels this month", pct: 8, ratio: "1/12" },
          { label: "1 essay per week", pct: 100, ratio: "1/1" },
        ].map((g) => (
          <div key={g.label}>
            <div className="flex justify-between text-xs mb-1.5" style={{ color: ink, opacity: 0.85 }}>
              <span>{g.label}</span>
              <span style={{ opacity: 0.6 }}>{g.ratio}</span>
            </div>
            <div className="h-1.5 rounded-full" style={{ background: `${ink}22` }}>
              <div className="h-full rounded-full transition-all duration-700" style={{ width: `${g.pct}%`, background: accent }} />
            </div>
          </div>
        ))}
      </div>
    ),
  },
];

export default function WalkthroughModal({ theme, onClose, onTryDemo }) {
  const [i, setI] = useState(0);
  const [playing, setPlaying] = useState(true);
  const t = theme;
  const ink = t.ink;
  const accent = t.seal;

  useEffect(() => {
    if (!playing) return;
    const id = setInterval(() => setI((x) => {
      // Stop auto-advancing once we reach the last scene — don't loop back.
      // Otherwise users on mobile keep missing the final Goals scene.
      if (x >= SCENES.length - 1) {
        setPlaying(false);
        return x;
      }
      return x + 1;
    }), 4500);
    return () => clearInterval(id);
  }, [playing]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowRight") { setI((x) => (x + 1) % SCENES.length); setPlaying(false); }
      if (e.key === "ArrowLeft") { setI((x) => (x - 1 + SCENES.length) % SCENES.length); setPlaying(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const scene = SCENES[i];
  const Icon = scene.icon;
  const Mock = scene.mock;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end md:items-center justify-center"
      style={{ background: "rgba(0,0,0,0.78)", backdropFilter: "blur(6px)" }}
      onClick={onClose}
      data-testid="walkthrough-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full md:max-w-4xl md:mx-6 rounded-t-3xl md:rounded-3xl overflow-hidden"
        style={{
          background: t.bg,
          border: `1px solid ${ink}1f`,
          boxShadow: "0 60px 120px rgba(0,0,0,0.7), 0 0 0 1px rgba(216,168,87,0.06)",
          maxHeight: "94vh",
        }}
      >
        <div className="flex items-center justify-between px-6 md:px-10 py-5" style={{ borderBottom: `1px solid ${ink}14` }}>
          <div className="flex items-center gap-3">
            <span style={{ display: "inline-block", width: 6, height: 6, borderRadius: 999, background: accent }} />
            <span className="text-[11px] uppercase tracking-[0.34em]" style={{ color: ink, opacity: 0.6 }}>
              The 60-second walkthrough
            </span>
          </div>
          <button onClick={onClose} className="w-9 h-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${ink}22`, color: ink }} data-testid="walkthrough-close">
            <X size={15} />
          </button>
        </div>

        {/* Progress dots */}
        <div className="px-6 md:px-10 pt-5 flex gap-1.5">
          {SCENES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setI(idx); setPlaying(false); }}
              className="h-1 rounded-full flex-1 transition cursor-pointer"
              style={{ background: idx === i ? accent : `${ink}22` }}
              data-testid={`walkthrough-dot-${idx}`}
              aria-label={`Scene ${idx + 1}`}
            />
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 md:gap-10 p-6 md:p-10 overflow-y-auto" style={{ maxHeight: "calc(94vh - 130px)" }}>
          {/* Copy column */}
          <div className="md:col-span-2 flex flex-col">
            <div className="flex items-center gap-3 mb-5">
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: `${accent}1a`, border: `1px solid ${accent}55` }}>
                <Icon size={16} style={{ color: accent }} strokeWidth={1.6} />
              </div>
              <span className="text-[11px] uppercase tracking-[0.34em]" style={{ color: ink, opacity: 0.55 }}>
                {scene.eyebrow}
              </span>
            </div>
            <h2
              className="leading-[1.04]"
              style={{
                fontFamily: "'Fraunces', serif",
                fontVariationSettings: "'opsz' 96, 'wght' 450",
                fontSize: "44px",
                letterSpacing: "-0.025em",
                color: ink,
              }}
            >
              {scene.title}<br />
              <span
                style={{
                  fontStyle: "italic",
                  fontVariationSettings: "'opsz' 96, 'wght' 500",
                  color: accent,
                }}
              >
                {scene.italic}
              </span>
            </h2>
            <p className="text-sm leading-[1.65] mt-5" style={{ color: ink, opacity: 0.72 }}>
              {scene.body}
            </p>
          </div>

          {/* Mock column */}
          <div className="md:col-span-3">
            <div className="rounded-2xl p-5 md:p-7" style={{ background: `${ink}06`, border: `1px solid ${ink}14`, minHeight: 280 }}>
              <Mock accent={accent} ink={ink} />
            </div>
          </div>
        </div>

        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 px-6 md:px-10 py-5" style={{ borderTop: `1px solid ${ink}14` }}>
          <div className="flex items-center justify-between md:justify-start gap-1.5">
            <div className="flex items-center gap-1.5">
              <button
                onClick={() => { setI((x) => (x - 1 + SCENES.length) % SCENES.length); setPlaying(false); }}
                className="w-11 h-11 md:w-9 md:h-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${ink}22`, color: ink, touchAction: "manipulation" }}
                data-testid="walkthrough-prev"
                aria-label="Previous"
              >
                <ChevronLeft size={17} />
              </button>
              <button
                onClick={() => setPlaying((p) => !p)}
                className="w-11 h-11 md:w-9 md:h-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${ink}22`, color: ink, touchAction: "manipulation" }}
                data-testid="walkthrough-play"
                aria-label={playing ? "Pause" : "Play"}
              >
                {playing ? <Pause size={14} /> : <Play size={14} />}
              </button>
              <button
                onClick={() => { setI((x) => (x + 1) % SCENES.length); setPlaying(false); }}
                className="w-11 h-11 md:w-9 md:h-9 rounded-full flex items-center justify-center" style={{ border: `1px solid ${ink}22`, color: ink, touchAction: "manipulation" }}
                data-testid="walkthrough-next"
                aria-label="Next"
              >
                <ChevronRight size={17} />
              </button>
            </div>
            <span className="text-[11px] md:ml-2" style={{ color: ink, opacity: 0.5 }}>
              {i + 1} / {SCENES.length}
            </span>
          </div>
          <button
            onClick={onTryDemo}
            className="rounded-full px-5 py-3 md:py-2.5 transition hover:opacity-90 inline-flex items-center justify-center gap-2 w-full md:w-auto"
            style={{ background: ink, color: t.bg, touchAction: "manipulation" }}
            data-testid="walkthrough-try-demo"
          >
            <Sparkles size={13} />
            <span className="text-[13px] font-medium tracking-tight">Try the live demo</span>
          </button>
        </div>
      </div>
    </div>
  );
}
